import {
  QuestionnaireFormProps,
  QuestionnaireQuestions,
} from '../../../models';
import { useEffect, useState } from 'react';

import styles from './CheckboxForm.module.scss';
import { Header } from '../../Screen';

export type CheckboxWithDescription = {
  title: string;
  description: string;
};

type Props<
  Questionnaire extends QuestionnaireQuestions,
  ValueType extends string
> = {
  mapValue: Record<ValueType, string | CheckboxWithDescription>;
} & QuestionnaireFormProps<Questionnaire, ValueType[]>;

export const CheckboxForm = <
  Questionnaire extends QuestionnaireQuestions,
  ValueType extends string
>({
  value,
  question,
  mapValue,
  onSubmitValue,
  featureName,
  twoColumnLayout = false,
  setValidForSubmission,
}: Props<Questionnaire, ValueType> & { twoColumnLayout?: boolean }) => {
  const [checkboxValues, setCheckboxValues] = useState<ValueType[]>(
    value ?? []
  );

  const hasNoneValue = Object.keys(mapValue).includes('NONE');

  useEffect(() => {
    const validForSubmission = checkboxValues && checkboxValues.length > 0;
    setValidForSubmission(validForSubmission);
  }, [checkboxValues, setValidForSubmission]);

  const handleOnChange = (newValue: ValueType) => {
    if (checkboxValues?.includes(newValue)) {
      const filteredCheckboxValues = checkboxValues.filter(
        (selectedValue) => selectedValue !== newValue
      );
      setCheckboxValues(filteredCheckboxValues);
      return;
    }

    if (hasNoneValue && newValue === 'NONE') {
      setCheckboxValues([newValue]);
      return;
    }

    if (hasNoneValue && newValue !== 'NONE') {
      const newValues = checkboxValues
        ? [...checkboxValues.filter((v) => v !== 'NONE'), newValue]
        : [newValue];
      setCheckboxValues(newValues);
      return;
    }

    const newValues = checkboxValues
      ? [...checkboxValues, newValue]
      : [newValue];
    setCheckboxValues(newValues);
  };

  const handleSubmit = () => {
    if (!checkboxValues || checkboxValues.length < 1) {
      throw new Error(
        `[${featureName}] Checkbox values are missing for submission from following options: ${Object.keys(
          mapValue
        ).join(', ')} for question: ${question}`
      );
    }
    onSubmitValue(checkboxValues);
  };

  const entries = Object.entries(mapValue) as [
    ValueType,
    string | CheckboxWithDescription
  ][];

  return (
    <Header onSubmit={handleSubmit}>
      <div
        className={`${
          twoColumnLayout
            ? `d-grid gap8 ${styles.grid} ${styles.wide}`
            : `d-flex fd-column gap8 ${styles.narrow}`
        }`}
      >
        {entries.map(([currentValue, label]) => (
          <div key={currentValue}>
            <input
              className="p-checkbox"
              id={currentValue}
              type="checkbox"
              value={currentValue}
              onChange={() => handleOnChange(currentValue)}
              checked={checkboxValues?.includes(currentValue)}
            />
            <label
              htmlFor={currentValue}
              className="p-label p-label--bordered pr16"
              data-cy={`checkbox-${currentValue}`}
            >
              {typeof label === 'string' ? (
                label
              ) : (
                <div>
                  <p className="p-p">{label.title}</p>
                  <span className="d-block p-p p-p--small tc-grey-600">
                    {label.description}
                  </span>
                </div>
              )}
            </label>
          </div>
        ))}
      </div>
    </Header>
  );
};
